/**
 * Template di offerta: i contenuti che alimentano il documento Word.
 *
 * Ogni template porta l'oggetto (uno per taglio), l'elenco attività per le
 * offerte di assistenza e le fasi per quelle di implementazione. In più può
 * avere un .docx di riferimento, per chi preferisce partire dalla propria
 * impaginazione invece che dai contenuti compilati qui.
 *
 * I 5 modelli storici sono stati travasati in tabella: modificarli qui vale
 * per tutte le offerte generate da qui in avanti.
 */
import { useEffect, useState } from 'react';
import { templateApi } from '../api';
import Modal from '../components/Modal';

const label = 'block text-xs font-medium text-gray-500 mb-0.5';
const aTesto = (r) => (r || []).join('\n');
const daTesto = (t) => String(t || '').split('\n').map(x => x.trim()).filter(Boolean);
const kb = (n) => n ? `${Math.round(n / 1024).toLocaleString('it-IT')} KB` : '';

const vuoto = () => ({
  nome: '', sigla: '', descrizione: '',
  oggetto: { assistenza: '', implementazione: '' },
  attivitaTesto: '', fasi: [], ordine: 100, attivo: true,
});

export default function TemplateOfferte() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(vuoto());
  const [saving, setSaving] = useState(false);
  const [caricando, setCaricando] = useState(null);

  const load = () => templateApi.list().then(setRows).catch(console.error);
  useEffect(() => { load(); }, []);

  const apriNuovo = () => { setForm(vuoto()); setModal({ mode: 'new' }); };
  const apriModifica = (t) => {
    setForm({
      ...t,
      oggetto: t.oggetto || { assistenza: '', implementazione: '' },
      attivitaTesto: aTesto(t.attivita),
      fasi: (t.fasi || []).map(f => ({ ...f, attivitaTesto: aTesto(f.attivita) })),
    });
    setModal({ mode: 'edit', id: t.id });
  };

  const salva = async () => {
    if (!form.nome?.trim()) return alert('Il nome è obbligatorio');
    setSaving(true);
    try {
      const payload = {
        nome: form.nome, sigla: form.sigla, descrizione: form.descrizione,
        oggetto: form.oggetto,
        attivita: daTesto(form.attivitaTesto),
        fasi: (form.fasi || []).map(f => ({
          nome: f.nome, durata: f.durata, attivita: daTesto(f.attivitaTesto),
        })),
        ordine: Number(form.ordine) || 100,
        attivo: form.attivo,
      };
      if (modal.mode === 'new') await templateApi.create(payload);
      else await templateApi.update(modal.id, payload);
      setModal(null); load();
    } catch (e) { alert(e); } finally { setSaving(false); }
  };

  const elimina = async (t) => {
    const n = (t.tipologie || []).length;
    const extra = n ? `\n\nÈ abbinato a ${n} tipologi${n === 1 ? 'a' : 'e'} (${t.tipologie.join(', ')}): l'abbinamento verrà tolto.` : '';
    if (!confirm(`Eliminare il template "${t.nome}"?${extra}`)) return;
    try { await templateApi.delete(t.id); load(); }
    catch (e) { alert(e); }
  };

  const commutaAttivo = async (t) => {
    try { await templateApi.update(t.id, { attivo: !t.attivo }); load(); }
    catch (e) { alert(e); }
  };

  const caricaFile = async (t, file) => {
    if (!file) return;
    setCaricando(t.id);
    try { await templateApi.caricaFile(t.id, file); load(); }
    catch (e) { alert(e); } finally { setCaricando(null); }
  };

  const togliFile = async (t) => {
    if (!confirm(`Togliere il file "${t.file_nome}" dal template "${t.nome}"?\n\nIl template resta, con i suoi contenuti.`)) return;
    try { await templateApi.eliminaFile(t.id); load(); }
    catch (e) { alert(e); }
  };

  const setFase = (i, k, v) => setForm(p => ({ ...p, fasi: p.fasi.map((x, j) => j === i ? { ...x, [k]: v } : x) }));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold text-gray-900">Template Offerte</h1>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            I contenuti che alimentano il documento Word: oggetto, attività e fasi di progetto.
            Restano modificabili anche in fase di stesura della singola offerta.
          </p>
        </div>
        <button className="btn-primary shrink-0" onClick={apriNuovo}>+ Nuovo template</button>
      </div>

      <div className="card p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-left text-xs text-gray-500">
              <th className="px-4 py-3">Template</th>
              <th className="px-4 py-3">Tipologie collegate</th>
              <th className="px-4 py-3 text-center">Attività</th>
              <th className="px-4 py-3 text-center">Fasi</th>
              <th className="px-4 py-3">File Word</th>
              <th className="px-4 py-3 text-center">Offerte</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-400">Nessun template</td></tr>
            )}
            {rows.map(t => (
              <tr key={t.id} className={`border-b last:border-0 hover:bg-gray-50 ${t.attivo ? '' : 'opacity-50'}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{t.nome}</div>
                  {t.descrizione && <div className="text-xs text-gray-500 max-w-[300px] truncate">{t.descrizione}</div>}
                  {!t.attivo && <span className="text-[10px] font-semibold text-gray-500">DISATTIVATO</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 flex-wrap">
                    {(t.tipologie || []).length === 0
                      ? <span className="text-xs text-gray-400">nessuna</span>
                      : t.tipologie.map(x => (
                          <span key={x} className="text-[11px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200">{x}</span>
                        ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-center text-gray-600">{(t.attivita || []).length || '—'}</td>
                <td className="px-4 py-3 text-center text-gray-600">{(t.fasi || []).length || '—'}</td>
                <td className="px-4 py-3 text-xs">
                  {t.file_nome ? (
                    <div className="flex items-center gap-2">
                      <button className="text-brand-600 hover:underline truncate max-w-[150px] text-left"
                              title={t.file_nome}
                              onClick={() => templateApi.scaricaFile(t.id, t.file_nome).catch(e => alert(e))}>
                        {t.file_nome}
                      </button>
                      <span className="text-gray-400">{kb(t.file_dimensione)}</span>
                      <button className="text-gray-400 hover:text-red-600" title="Togli il file"
                              onClick={() => togliFile(t)}>✕</button>
                    </div>
                  ) : (
                    <label className="text-brand-600 hover:underline cursor-pointer whitespace-nowrap">
                      {caricando === t.id ? 'caricamento…' : '+ carica .docx'}
                      <input type="file" accept=".doc,.docx" className="hidden"
                             onChange={e => caricaFile(t, e.target.files?.[0])} />
                    </label>
                  )}
                </td>
                <td className="px-4 py-3 text-center text-gray-500">{t.offerte_collegate || '—'}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button className="text-xs text-brand-600 hover:underline" onClick={() => apriModifica(t)}>modifica</button>
                  <button className="text-xs text-gray-500 hover:underline ml-3" onClick={() => commutaAttivo(t)}>
                    {t.attivo ? 'disattiva' : 'riattiva'}
                  </button>
                  <button className="text-xs text-gray-400 hover:text-red-600 ml-3" onClick={() => elimina(t)}>elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={modal.mode === 'new' ? 'Nuovo template' : 'Modifica template'}
               onClose={() => setModal(null)} size="xl">
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <label className={label}>Nome *</label>
                <input className="input text-sm" value={form.nome}
                       placeholder="es. ISO 50001 — Energia"
                       onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Sigla</label>
                <input className="input text-sm" value={form.sigla || ''} placeholder="es. SGE"
                       onChange={e => setForm(p => ({ ...p, sigla: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Ordine nei menu</label>
                <input className="input text-sm" type="number" value={form.ordine ?? 100}
                       onChange={e => setForm(p => ({ ...p, ordine: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className={label}>Descrizione interna</label>
              <input className="input text-sm" value={form.descrizione || ''}
                     placeholder="A cosa serve questo template — non compare nel documento"
                     onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className={label}>Oggetto — offerta di assistenza</label>
                <textarea className="input text-sm" rows={2} value={form.oggetto?.assistenza || ''}
                          placeholder="Usa {ANNO} per l'anno corrente"
                          onChange={e => setForm(p => ({ ...p, oggetto: { ...p.oggetto, assistenza: e.target.value } }))} />
              </div>
              <div>
                <label className={label}>Oggetto — offerta di implementazione</label>
                <textarea className="input text-sm" rows={2} value={form.oggetto?.implementazione || ''}
                          placeholder="Usa {ANNO} per l'anno corrente"
                          onChange={e => setForm(p => ({ ...p, oggetto: { ...p.oggetto, implementazione: e.target.value } }))} />
              </div>
            </div>

            <div>
              <label className={label}>Attività proposte per l'assistenza <span className="font-normal">(una per riga)</span></label>
              <textarea className="input font-mono text-xs" rows={7} value={form.attivitaTesto || ''}
                        onChange={e => setForm(p => ({ ...p, attivitaTesto: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                Fasi proposte per l'implementazione
              </div>
              {(form.fasi || []).map((f, i) => (
                <div key={i} className="bg-gray-50 border rounded-lg p-2.5 space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <label className={label}>Nome fase</label>
                      <input className="input text-sm" value={f.nome || ''}
                             onChange={e => setFase(i, 'nome', e.target.value)} />
                    </div>
                    <div className="w-44">
                      <label className={label}>Durata (testo)</label>
                      <input className="input text-sm" value={f.durata || ''} placeholder="es. da 2 a 3 mesi"
                             onChange={e => setFase(i, 'durata', e.target.value)} />
                    </div>
                    <button type="button" title="Elimina fase"
                            className="text-gray-400 hover:text-red-600 text-lg leading-none mt-5 px-1"
                            onClick={() => setForm(p => ({ ...p, fasi: p.fasi.filter((_, j) => j !== i) }))}>✕</button>
                  </div>
                  <div>
                    <label className={label}>Attività della fase (una per riga)</label>
                    <textarea className="input font-mono text-xs" rows={4} value={f.attivitaTesto || ''}
                              onChange={e => setFase(i, 'attivitaTesto', e.target.value)} />
                  </div>
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm"
                      onClick={() => setForm(p => ({ ...p, fasi: [...(p.fasi || []), { nome: `Fase ${(p.fasi || []).length + 1}`, durata: '', attivitaTesto: '' }] }))}>
                + Aggiungi fase
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button className="btn-secondary" onClick={() => setModal(null)}>Annulla</button>
            <button className="btn-primary" onClick={salva} disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva template'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
